import type { ShopifyAdminAppRouter } from "@loot-os/functions/src/dashboard-api/trpc/shopifyAdmin/router.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { inferRouterOutputs } from "@trpc/server";
import { useEffect, useRef } from "react";
import { useTRPC } from "./trpc";

type IneligibleReason = NonNullable<
  inferRouterOutputs<ShopifyAdminAppRouter>["status"]["ineligibleReason"]
>;

const INELIGIBLE: Record<IneligibleReason, string> = {
  currency:
    "Loot Boxes needs a store that prices in US dollars. Change the store currency to USD in Settings → Markets, then try again.",
  legacy_customer_accounts:
    "Loot Boxes needs new customer accounts, which hold store credit. Switch to them in Settings → Customer accounts, then try again.",
  store_credit_disabled:
    "Loot Boxes pays shoppers in store credit. Turn store credit on for this store, then try again.",
};

/**
 * The first run of an install: sets the shop up (its own Loot OS account, then the product
 * import) with no input from the merchant. A store the app cannot serve is told what to change,
 * and can try again from here.
 */
export function SetupScreen({
  status,
}: {
  status: { shopDomain: string; ineligibleReason: IneligibleReason | null };
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const setup = useMutation(
    trpc.setup.mutationOptions({
      onSuccess: (result) => {
        shopify.toast.show(
          result.complete
            ? `Ready. Imported ${result.variants} variants.`
            : "Ready. Your products finish importing within a few minutes.",
        );
        void queryClient.invalidateQueries();
      },
    }),
  );
  const started = useRef(false);
  const eligible = status.ineligibleReason === null;

  useEffect(() => {
    if (!eligible || started.current) return;
    started.current = true;
    setup.mutate();
  }, [eligible, setup]);

  return (
    <s-page inlineSize="large" heading="Setting up Loot Boxes">
      {status.ineligibleReason ? (
        <s-stack gap="base">
          <s-banner tone="warning">{INELIGIBLE[status.ineligibleReason]}</s-banner>
          <s-stack direction="inline">
            <s-button
              onClick={() =>
                void queryClient.invalidateQueries({ queryKey: trpc.status.queryKey() })
              }
            >
              Check again
            </s-button>
          </s-stack>
        </s-stack>
      ) : setup.isError ? (
        <s-stack direction="inline">
          <s-button variant="primary" onClick={() => setup.mutate()}>
            Try again
          </s-button>
        </s-stack>
      ) : (
        <s-paragraph>
          Setting up {status.shopDomain} and importing your products. This takes a few seconds.
        </s-paragraph>
      )}
    </s-page>
  );
}
